import { api, type GetPaths } from '@/api/client';
import type { OutboxHolder } from './outbox';

/**
 * The server's change feed, read for Inventory & Stores (.docs/021, .docs/023
 * step 5).
 *
 * The feed says that a record changed, not what it now holds, and it may say
 * so twice but never skips. That is all this needs: when anything a stock tab
 * shows has changed, the stock data on this PC is read again. The cursor is
 * kept in the person's copy, so a reload carries on from where it was.
 */

/** Every table a stock tab shows something from. */
export const INVENTORY_TABLES: ReadonlySet<string> = new Set([
  'stock_balances',
  'stock_bin_balances',
  'stock_batches',
  'inward_shipments',
  'consumption_log',
  'stock_transfers',
  'stock_adjustments',
  'stock_demands',
  'skus',
  'sku_categories',
  'uoms',
  'suppliers',
  'stores',
  'storage_bins',
]);

const CURSOR = 'feed.cursor';
const PAGE = 500;
/** A feed that always says there is more is read again next time. */
const MOST_PAGES = 20;

interface Page {
  cursor: string;
  changes: Array<{ entity: string }>;
  hasMore: boolean;
  mustReload: boolean;
}

/**
 * Reads the feed from where this PC got to. True when something a stock tab
 * shows has changed — or the feed says to start again — since last time.
 */
export async function stockChanged(store: Pick<OutboxHolder, 'read' | 'write'>): Promise<boolean> {
  let cursor = await store.read<string>(CURSOR);
  if (!cursor) {
    // A first read here: start from now. The warm-up has just loaded it all.
    const start = (await api.get('/api/v1/sync/start' as GetPaths)) as unknown as { cursor: string };
    await store.write(CURSOR, start.cursor);
    return false;
  }

  let changed = false;
  for (let pages = 0; pages < MOST_PAGES; pages += 1) {
    const page = (await api.get('/api/v1/sync/changes' as GetPaths, { since: cursor, limit: PAGE })) as unknown as Page;
    if (page.mustReload) {
      // Away longer than the server keeps changes, or after a reset: what is
      // here may be out of date in ways the feed can no longer list.
      const start = (await api.get('/api/v1/sync/start' as GetPaths)) as unknown as { cursor: string };
      cursor = start.cursor;
      changed = true;
      break;
    }
    if (page.changes.some((change) => INVENTORY_TABLES.has(change.entity))) changed = true;
    cursor = page.cursor;
    if (!page.hasMore) break;
  }
  await store.write(CURSOR, cursor);
  return changed;
}
